import { Area } from "react-easy-crop";
import {
    Action,
    Color,
    Designer,
    Employee,
    Manufacturer,
    Material,
    OptionsArray,
    OptionsObject,
    ProductStatus,
} from "../types";

export const isGlobalAdmin = (employee: Employee) => {
    if (employee.role === "master" || employee.role === "superAdmin") {
        return true;
    }
    return false;
};

export const isAdmin = (employee: Employee) => {
    if (employee.role === "admin" || employee.role === "master" || employee.role === "superAdmin") {
        return true;
    }
    return false;
};

export const getRolePriority = (role: string) => {
    switch (role) {
        case "superAdmin":
            return 0;
        case "master":
            return 1;
        case "admin":
            return 2;
        case "user":
            return 3;
        default:
            return 4;
    }
};

// Sorts A-Z by the given label, using Swedish rules (Å, Ä, Ö after Z) and ignoring case.
export const sortByLabel = <T>(list: readonly T[], getLabel: (item: T) => string): T[] =>
    [...list].sort((a, b) => getLabel(a).localeCompare(getLabel(b), "sv", { sensitivity: "base" }));

export const sortByName = <T extends { name: string }>(list: readonly T[]): T[] =>
    sortByLabel(list, item => item.name);

// Materials and categories store Swedish in `name`.
export const getTranslatedName = (
    item: {
        name?: string | null;
        nameEN?: string | null;
        nameNO?: string | null;
        nameDK?: string | null;
        nameFI?: string | null;
    } | null,
    language?: string,
): string => {
    if (!item) return "";

    switch (language) {
        case "sv":
            return item.name || "";
        case "en":
            return item.nameEN || "";
        case "no":
            return item.nameNO || "";
        case "dk":
            return item.nameDK || "";
        case "fi":
            return item.nameFI || "";
        default:
            return item.nameEN || item.name || "";
    }
};

// Statuses and actions store English in `name`.
export const getStatusLabel = (option: OptionsObject | null, language?: string): string => {
    if (!option) return "";

    switch (language) {
        case "sv":
            return option.nameSV || "";
        case "en":
            return option.name || "";
        case "no":
            return option.nameNO || "";
        case "dk":
            return option.nameDK || "";
        case "fi":
            return option.nameFI || "";
        default:
            return option.name || "";
    }
};

// Checks if the given string is a valid email by following pattern,
// anystring@anystring.anystring
export const isValidEmail = (email: string) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
};

export const isValidPassword = (password: string) => {
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
    return strongPasswordRegex.test(password);
};

export const createReadOnlySortedOptions = (
    array: Manufacturer[] | Designer[] | ProductStatus[] | Action[],
) => {
    const a = array
        .map(item => {
            return { label: item.name, id: item.id };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    const b: OptionsArray = a;
    return b;
};

export const createReadOnlySortedOptionsMaterial = (array: Material[]) => {
    const a = array
        .map(item => {
            return {
                label: item.name,
                id: item.id,
                nameEN: item.nameEN,
                nameNO: item.nameNO,
                nameDK: item.nameDK,
                nameFI: item.nameFI,
                name: item.name,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    const b: OptionsArray = a;
    return b;
};
export const createReadOnlySortedOptionsProductStatuses = (array: ProductStatus[]) => {
    const a = array
        .map(item => {
            return {
                label: item.name,
                id: item.id,
                name: item.name,
                nameNO: item.nameNO,
                nameDK: item.nameDK,
                nameFI: item.nameFI,
                nameSV: item.nameSV,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    const b: OptionsArray = a;
    return b;
};

export const createReadOnlyOptions = (
    array: (Manufacturer | Designer | ProductStatus | Action)[],
) => {
    const a = array.map(item => {
        return {
            label: item.name,
            id: item.id,
        };
    });
    const b: OptionsArray = a;
    return b;
};

export const createReadOnlyMaterialsOptions = (array: Material[]) => {
    const a = array.map(item => {
        return {
            name: item.name,
            id: item.id,
            nameEN: item.nameEN,
            nameNO: item.nameNO,
            nameDK: item.nameDK,
            nameFI: item.nameFI,
            label: item.name,
        };
    });
    const b: OptionsArray = a;
    return b;
};

export const createReadOnlyProductStatusesOptions = (array: ProductStatus[] | Action[]) => {
    const a = array.map(item => {
        return {
            name: item.name,
            id: item.id,
            nameSV: item.nameSV,
            nameNO: item.nameNO,
            nameDK: item.nameDK,
            nameFI: item.nameFI,
            label: item.name,
        };
    });
    const b: OptionsArray = a;
    return b;
};

export const createImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", error => reject(error));
        image.src = url;
    });

export function getRadianAngle(degreeValue: number) {
    return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
    const rotRad = getRadianAngle(rotation);

    return {
        width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
        height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
}

export function readFile(file: Blob, callback: (result: string) => void) {
    const reader = new FileReader();
    reader.onloadend = () => callback(reader.result as string);
    reader.readAsDataURL(file);
}

export async function getCroppedImg(
    imageSrc: string,
    pixelCrop: Area,
    rotation = 0,
    flip = { horizontal: false, vertical: false },
) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        return "";
    }

    const rotRad = getRadianAngle(rotation);

    // calculate bounding box of the rotated image
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
        image.width,
        image.height,
        rotation,
    );

    // set canvas size to match the bounding box
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    // translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);

    // draw rotated image
    ctx.drawImage(image, 0, 0);

    // croppedAreaPixels values are bounding box relative
    // extract the cropped image using these values
    const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotate image at the top left corner
    ctx.putImageData(data, 0, 0);

    // As Base64 string
    // return canvas.toDataURL('image/jpeg');

    // As a blob
    return new Promise<string>((resolve, reject) => {
        canvas.toBlob(file => {
            if (!file) {
                reject(new Error("Canvas is empty"));
                return "";
            }
            resolve(URL.createObjectURL(file));
        }, "image/jpeg");
    });
}

export async function getRotatedImage(imageSrc: string, rotation = 0) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return null;
    }

    const orientationChanged =
        rotation === 90 || rotation === -90 || rotation === 270 || rotation === -270;
    if (orientationChanged) {
        canvas.width = image.height;
        canvas.height = image.width;
    } else {
        canvas.width = image.width;
        canvas.height = image.height;
    }

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);

    return new Promise((resolve, reject) => {
        canvas.toBlob(file => {
            if (!file) {
                reject(new Error("Canvas is empty"));
                return;
            }
            resolve(URL.createObjectURL(file));
        }, "image/png");
    });
}

// This is for cleaning up the color string and add the new value as an array "colors" for the product
export const normalizeColors = (rawColor: string, colors: Color[]): string[] => {
    if (!rawColor) return [];

    const normalized = rawColor
        .toLowerCase()
        .replace(/[^a-zåäö\s/]/gi, "")
        .replace(/\s*(\/|\\|,| och | med | o )\s*/g, "/");

    const parts = normalized
        .split("/")
        .flatMap(p => p.split(" "))
        .map(w => w.trim())
        .filter(w => w.length > 0);

    const matchedColors = new Set<string>();

    for (const word of parts) {
        const mappedName = colorMap[word];
        if (!mappedName) continue;

        const match = colors.find(c => c.name.toLowerCase() === mappedName.toLowerCase());
        if (match) {
            matchedColors.add(match.id);
        }
    }

    return Array.from(matchedColors);
};

export const sx = {
    "& .MuiInputBase-root": {
        fontFamily: "'Yllw Sans a1 241219', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        color: "black",
    },
    "& .MuiInputLabel-root": {
        fontFamily: "'Yllw Sans a1 241219', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        color: "rgba(0, 0, 0, 0.50)",
    },
    "& .MuiInputBase-input::placeholder": {
        fontFamily: "'Yllw Sans a1 241219', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        color: "rgba(0, 0, 0, 0.50)",
    },
};

export const typographySx = {
    fontSize: "16px",
    fontFamily: "'Yllw Sans a1 241219', sans-serif",
    fontWeight: "400",
    color: "gray",
};

const colorMap: { [key: string]: string } = {
    // svart, blå, grön, röd, gul, vit, grå, orange, brun, lila, rosa
    svart: "Svart",
    svarta: "Svart",
    svartlack: "Svart",
    blå: "Blå",
    ljusblå: "Blå",
    mörkblå: "Blå",
    turkos: "Blå",
    blågrå: "Blå",
    petroleum: "Blå",
    blåbets: "Blå",
    blålila: "Blå",
    grön: "Grön",
    ljusgrön: "Grön",
    mörkgrön: "Grön",
    mintgrön: "Grön",
    mossgrön: "Grön",
    limegrön: "Grön",
    teal: "Grön",
    kamoflage: "Grön",
    röd: "Röd",
    mörkröd: "Röd",
    ljusröd: "Röd",
    vinröd: "Röd",
    roströd: "Röd",
    körsbär: "Röd",
    burgundy: "Röd",
    oxblod: "Röd",
    rött: "Röd",
    tegelröd: "Röd",
    gul: "Gul",
    ljusgul: "Gul",
    mörkgul: "Gul",
    guld: "Gul",
    ocra: "Gul",
    gulbrun: "Gul",
    senap: "Gul",
    vit: "Vit",
    vita: "Vit",
    vitt: "Vit",
    helvitt: "Vit",
    vitlack: "Vit",
    glas: "Vit",
    naturvit: "Vit",
    vitbets: "Vit",
    elfenbensvit: "Vit",
    white: "Vit",
    grå: "Grå",
    gråa: "Grå",
    grått: "Grå",
    grey: "Grå",
    ljusgrå: "Grå",
    mellangrå: "Grå",
    mörkgrå: "Grå",
    rostfritt: "Grå",
    kalksten: "Grå",
    ljusbeige: "Grå",
    betong: "Grå",
    krom: "Grå", // ??
    chrome: "Grå", // ??
    metall: "Grå",
    silver: "Grå",
    stål: "Grå",
    ask: "Grå",
    natur: "Grå",
    orange: "Orange",
    ljusorange: "Orange",
    mörkorange: "Orange",
    aprikos: "Orange",
    brun: "Brun",
    mörkbrun: "Brun",
    ljusbrun: "Brun",
    mässing: "Brun",
    koppar: "Brun",
    kopparfärg: "Brun",
    bok: "Brun",
    ek: "Brun",
    björkmasur: "Brun",
    furu: "Brun",
    valnöt: "Brun",
    kork: "Brun",
    björk: "Brun",
    trä: "Brun",
    beige: "Brun",
    lila: "Lila",
    mörklila: "Lila",
    ljuslila: "Lila",
    aubergine: "Lila",
    rosa: "Rosa",
    ljusrosa: "Rosa",
    mörkrosa: "Rosa",
    cerise: "Rosa",
    ceriserosa: "Rosa",
};
